import { useCallback, useEffect, useState } from "react";
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody, TablePagination,
  TextField, MenuItem, Button, Chip, Stack, Dialog, IconButton, Snackbar, Alert, useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { HandForm, CSS as HAND_FORM_CSS } from "./index.js";
import { useSelector } from "react-redux";
import { axiosRequest } from "../../utility/axios";
import { apiBaseUrl } from "../../constant/endpoint";
import { HANDS, handLabel } from "./hands.js";

// CMS page (admins, behind the normal CMS login) listing every hand dealers have logged
// from /dealer-hand. Voiding an entry (the floor in-charge's call on a dispute or a
// mistake) removes it from the High Hand winner. Add Hand reuses the dealer page's form.
const fmtTime = (d) => new Date(d).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });

export default function DealerHandAdminPage() {
  const token = useSelector((state) => state.loginSlice.token);
  const call = useCallback((method, url, body) => axiosRequest(method, url, token, body), [token]);
  const [entries, setEntries] = useState([]);
  const [counts, setCounts] = useState({});
  const [pagination, setPagination] = useState({});
  const [page, setPage] = useState(0);
  const rowsPerPage = 20;
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [hand, setHand] = useState("");
  const [player, setPlayer] = useState("");
  const [dealer, setDealer] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const isMobile = useMediaQuery(useTheme().breakpoints.down("md"));

  // Adapter so the dealer page's HandForm can post through this page's PIN session.
  const formApi = useCallback(async (method, path, data) => {
    const res = await call(method.toUpperCase(), `${apiBaseUrl}${path}`, data);
    if (!res?.data) return { ok: false, error: res?.message || "Network error" };
    return { ok: true, payload: res.data };
  }, [call]);

  const fetchEntries = useCallback(async () => {
    const q = new URLSearchParams({ page: page + 1, size: rowsPerPage });
    if (dateFrom) q.set("date_from", dateFrom);
    if (dateTo) q.set("date_to", dateTo);
    if (hand) q.set("hand", hand);
    if (player.trim()) q.set("player", player.trim());
    if (dealer.trim()) q.set("dealer", dealer.trim());
    const res = await call("GET", `${apiBaseUrl}/dealer-hands?${q}`);
    setEntries(res?.data?.entries || []);
    setCounts(res?.data?.counts || {});
    setPagination(res?.data?.pagination || {});
  }, [call, page, dateFrom, dateTo, hand, player, dealer]);

  useEffect(() => {
    const t = setTimeout(fetchEntries, 300);
    return () => clearTimeout(t);
  }, [fetchEntries]);

  const toggleVoid = async (entry) => {
    const voiding = !entry.voided;
    if (voiding && !window.confirm(`Void ${entry.hand_desc || handLabel(entry.hand)} for ${entry.player_id}? It will no longer count for High Hand.`)) return;
    setBusyId(entry._id);
    await call("PATCH", `${apiBaseUrl}/dealer-hands/${entry._id}/void`, { voided: voiding });
    setBusyId(null);
    fetchEntries();
  };

  const resetPage = (setter) => (e) => { setter(e.target.value); setPage(0); };
  const hasFilters = dateFrom || dateTo || hand || player || dealer;

  const voidChip = (e) => e.voided && (
    <Chip size="small" color="error" label={`Voided${e.voided_by ? ` by ${e.voided_by}` : ""}`} />
  );
  const voidButton = (e) => (
    <Button size="small" color={e.voided ? "primary" : "error"} disabled={busyId === e._id} onClick={() => toggleVoid(e)}>
      {e.voided ? "Restore" : "Void"}
    </Button>
  );

  return (
    <Box sx={{ width: "100%", overflowX: "hidden", marginTop: { xs: "55px", md: "95px" } }}>
      <Box sx={{ padding: { xs: "16px", md: "32px" } }}>
        <Box mb={{ xs: 2, md: 3 }} display="flex" justifyContent="space-between" alignItems="flex-start" gap={1.5} flexWrap="wrap">
          <Box sx={{ minWidth: 0 }}>
            <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">Dealer Hands</Typography>
            <Typography color="text.secondary" fontSize={{ xs: 13, md: 16 }}>Full House or better, logged by dealers</Typography>
          </Box>
        </Box>

        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)} fullWidth={isMobile}
          sx={{ mb: 2, borderRadius: 2, height: 44 }}>
          Add Hand
        </Button>

        {/* counts double as quick hand filters; scroll sideways on phones instead of wrapping */}
        <Stack direction="row" gap={1} mb={2} sx={{ flexWrap: { xs: "nowrap", md: "wrap" }, overflowX: { xs: "auto", md: "visible" }, pb: { xs: 0.5, md: 0 } }}>
          {HANDS.map((h) => (
            <Chip key={h.key} label={`${handLabel(h.key)}: ${counts[h.key] ?? 0}`} variant={hand === h.key ? "filled" : "outlined"} color="primary"
              sx={{ flexShrink: 0 }} onClick={() => { setHand(hand === h.key ? "" : h.key); setPage(0); }} />
          ))}
        </Stack>

        <Box mb={2} sx={{ display: "grid", gap: { xs: 1.5, md: 2 }, alignItems: "center",
          gridTemplateColumns: { xs: "1fr 1fr", md: "160px 160px 220px 200px 200px auto" } }}>
          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={dateFrom} onChange={resetPage(setDateFrom)} />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={dateTo} onChange={resetPage(setDateTo)} />
          <TextField size="small" select label="Hand" value={hand} onChange={resetPage(setHand)} sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}>
            <MenuItem value="">All hands</MenuItem>
            {HANDS.map((h) => <MenuItem key={h.key} value={h.key}>{handLabel(h.key)}</MenuItem>)}
          </TextField>
          <TextField size="small" label="Player ID / name" value={player} onChange={resetPage(setPlayer)} />
          <TextField size="small" label="Dealer" value={dealer} onChange={resetPage(setDealer)} />
          {hasFilters && (
            <Button size="small" sx={{ gridColumn: { xs: "1 / -1", md: "auto" }, justifySelf: { xs: "stretch", md: "start" } }}
              onClick={() => { setDateFrom(""); setDateTo(""); setHand(""); setPlayer(""); setDealer(""); setPage(0); }}>Clear filters</Button>
          )}
        </Box>

        {isMobile ? (
          <Box>
            {!entries.length ? (
              <Paper sx={{ borderRadius: 3, py: 4, textAlign: "center", color: "text.secondary" }}>No entries</Paper>
            ) : entries.map((e) => (
              <Paper key={e._id} sx={{ borderRadius: 3, p: 1.75, mb: 1.25, opacity: e.voided ? 0.6 : 1 }}>
                <Typography fontWeight="bold" fontSize={15} sx={{ wordBreak: "break-word" }}>{e.hand_desc || handLabel(e.hand)}</Typography>
                <Typography fontSize={14} mt={0.5} sx={{ wordBreak: "break-word" }}>
                  <span style={e.voided ? { textDecoration: "line-through" } : undefined}>{e.player_id}</span>
                  {e.player_name ? ` — ${e.player_name}` : ""}
                </Typography>
                <Typography color="text.secondary" fontSize={12.5} mt={0.25}>{fmtTime(e.createdAt)} · Dealer: {e.dealer_name}</Typography>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mt={1} flexWrap="wrap">
                  <Box>{voidChip(e)}</Box>
                  {voidButton(e)}
                </Box>
              </Paper>
            ))}
            <TablePagination
              component="div" count={pagination.total || 0} page={page} rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[rowsPerPage]} onPageChange={(_, p) => setPage(p)}
              sx={{ ".MuiTablePagination-toolbar": { px: 0 } }}
            />
          </Box>
        ) : (
          <Paper sx={{ borderRadius: 3, overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Time (IST)</TableCell>
                  <TableCell>Player ID</TableCell>
                  <TableCell>Player Name</TableCell>
                  <TableCell>Hand</TableCell>
                  <TableCell>Dealer</TableCell>
                  <TableCell align="right">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!entries.length ? (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>No entries</TableCell></TableRow>
                ) : entries.map((e) => (
                  <TableRow key={e._id} sx={e.voided ? { opacity: 0.55 } : undefined}>
                    <TableCell>{fmtTime(e.createdAt)}</TableCell>
                    <TableCell sx={e.voided ? { textDecoration: "line-through" } : undefined}>{e.player_id}</TableCell>
                    <TableCell>{e.player_name || "—"}</TableCell>
                    <TableCell><b>{e.hand_desc || handLabel(e.hand)}</b></TableCell>
                    <TableCell>{e.dealer_name}</TableCell>
                    <TableCell align="right">
                      <Box display="inline-flex" alignItems="center" gap={1}>{voidChip(e)}{voidButton(e)}</Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div" count={pagination.total || 0} page={page} rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[rowsPerPage]} onPageChange={(_, p) => setPage(p)}
            />
          </Paper>
        )}
      </Box>

      {/* Add Hand -- the same form dealers use on /dealer-hand, saved under this login's name */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullScreen={isMobile} fullWidth maxWidth="sm"
        PaperProps={{ sx: { background: "#0f1115", color: "#f2f2f2" } }}>
        <style>{HAND_FORM_CSS}</style>
        <Box className="dh-header" sx={{ position: "sticky", top: 0 }}>
          <h1>🃏 Add Hand</h1>
          <IconButton onClick={() => setAddOpen(false)} sx={{ color: "#c9cdd6" }} aria-label="Close"><CloseIcon /></IconButton>
        </Box>
        <Box className="dh-body" sx={{ width: "100%" }}>
          <HandForm
            api={formApi}
            paths={{ entries: "/dealer-hands", lookup: "/dealer-hands/player-lookup" }}
            showRecent={false}
            showToast={(msg, err) => setToast({ msg, err })}
            onSaved={() => { setAddOpen(false); fetchEntries(); }}
          />
        </Box>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {toast ? <Alert severity={toast.err ? "error" : "success"} variant="filled" onClose={() => setToast(null)}>{toast.msg}</Alert> : <span />}
      </Snackbar>
    </Box>
  );
}
